import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
} from "@mui/material";

import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type {
  ChargerConfig,
  ChargerGroup,
  ChargerModel,
  ConfigTemplate,
  CountedPage,
} from "../types";
import { useToast } from "../toast";

interface Props {
  open: boolean;
  template: ConfigTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function ConfigTemplateDialog({ open, template, onClose, onSaved }: Props) {
  const api = useAuthApi();
  const toast = useToast();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [jsonText, setJsonText] = useState("{}");
  const [parseError, setParseError] = useState<string | null>(null);
  const [chargers, setChargers] = useState<ChargerConfig[]>([]);
  const [groups, setGroups] = useState<ChargerGroup[]>([]);
  const [models, setModels] = useState<ChargerModel[]>([]);
  const [chargerIds, setChargerIds] = useState<number[]>([]);
  const [groupIds, setGroupIds] = useState<number[]>([]);
  const [modelIds, setModelIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(template?.name ?? "");
    setDescription(template?.description ?? "");
    setJsonText(JSON.stringify(template?.config_values ?? {}, null, 2));
    setChargerIds(template?.charger_config_ids ?? []);
    setGroupIds(template?.charger_group_ids ?? []);
    setModelIds(template?.charger_model_ids ?? []);
    setParseError(null);
    Promise.all([
      api<CountedPage<ChargerConfig>>(`${MGMT}/charger-configs`),
      api<ChargerGroup[]>(`${MGMT}/charger-groups`),
      api<ChargerModel[]>(`${MGMT}/charger-models`),
    ])
      .then(([cfgs, chGroups, chModels]) => {
        setChargers(cfgs?.items ?? []);
        setGroups(chGroups ?? []);
        setModels(chModels ?? []);
      })
      .catch((e) => toast.show((e as Error).message, "error"));
  }, [open, template]);

  const save = async () => {
    setSaving(true);
    try {
      let configValues: Record<string, string | number | boolean>;
      try {
        const parsed = JSON.parse(jsonText || "{}");
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          throw new Error("Config must be a JSON object");
        }
        configValues = parsed as Record<string, string | number | boolean>;
      } catch (e) {
        setParseError((e as Error).message);
        setSaving(false);
        return;
      }

      const body = {
        name,
        description: description || null,
        config_values: configValues,
        charger_config_ids: chargerIds,
        charger_group_ids: groupIds,
        charger_model_ids: modelIds,
      };

      if (template) {
        await api(`${MGMT}/config-templates/${template.id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
        toast.show("Template updated");
      } else {
        await api(`${MGMT}/config-templates`, {
          method: "POST",
          body: JSON.stringify(body),
        });
        toast.show("Template created");
      }
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{template ? "Edit Config Template" : "Add Config Template"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Template Name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <TextField
            label="OCPP Configuration JSON"
            multiline
            minRows={8}
            value={jsonText}
            onChange={(e) => {
              setJsonText(e.target.value);
              setParseError(null);
            }}
          />
          <FormControl fullWidth>
            <InputLabel>Assign Chargers</InputLabel>
            <Select
              multiple
              value={chargerIds}
              onChange={(e) => setChargerIds(e.target.value as number[])}
              input={<OutlinedInput label="Assign Chargers" />}
              renderValue={(selected) => `${selected.length} selected`}
            >
              {chargers.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  <ListItemText primary={c.charge_point_id} secondary={c.display_name || c.location?.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Assign Charger Groups</InputLabel>
            <Select
              multiple
              value={groupIds}
              onChange={(e) => setGroupIds(e.target.value as number[])}
              input={<OutlinedInput label="Assign Charger Groups" />}
              renderValue={(selected) => `${selected.length} selected`}
            >
              {groups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  <ListItemText primary={g.name} secondary={`${g.chargers.length} chargers`} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Assign Charger Models</InputLabel>
            <Select
              multiple
              value={modelIds}
              onChange={(e) => setModelIds(e.target.value as number[])}
              input={<OutlinedInput label="Assign Charger Models" />}
              renderValue={(selected) => `${selected.length} selected`}
            >
              {models.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  <ListItemText primary={`${m.vendor} ${m.model_name}`} secondary={m.description || ""} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {parseError && <Alert severity="error">{parseError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || !name.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
